import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { withTheme } from 'styled-components';
import {
    Typography,
    Button,
    Grid,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar as MuiAvatar,
    Tooltip,
} from '@mic3/platform-ui';

import ModalDialog from 'app/components/organisms/ModalDialog/ModalDialog';
import Icon from 'app/components/atoms/Icon/Icon';
import { CardDivider } from 'app/containers/Entities/Relationships/RelationItem';
import { get } from 'app/utils/lo/lo';
import { getAttachmentUrl } from 'app/utils/attachments/attachmentsUtils';
import { copyToClipboard } from 'app/utils/classification/classificationUtils';
import { formatDate } from 'app/utils/date/date';
import { loadMessageReadReceipts } from 'store/actions/chat/chatActions';
import { showToastr } from 'store/actions/app/appActions';

const StyledModalDialog = styled(ModalDialog)`
    & .MuiDialog-paper {
        color: ${({ theme }) => theme.material.colors.text.primary};
        max-width: 976px;
    }
    *::-webkit-scrollbar {
        width: ${({ theme }) => theme.material.colors.scrollbar.width};
        height: ${({ theme }) => theme.material.colors.scrollbar.height};
    }
    *::-webkit-scrollbar-thumb {
        border-radius: ${({ theme }) => theme.material.colors.scrollbar.thumb.radius};
        background-color: ${({ theme }) => theme.material.colors.scrollbar.thumb.background};
        min-height: 65px;
    }
    *::-webkit-scrollbar-track {
        background-color: ${({ theme }) => theme.material.colors.scrollbar.track.background};
    }
`;

const TitleText = styled.span`
    font-family: 'Roboto', sans-serif;
    font-weight: 500;
    font-size: 1.25rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.material.colors.text.title};
`;

const CloseButton = styled(Button)`
    && {
        color: ${({ theme }) => theme.material.colors.secondary.main};
        font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        letter-spacing: 0.49px;
        text-transform: uppercase;
        min-width: auto;
    }
    @media (max-width: 481px) {
        && {
            display: none;
        }
    }
`;

const CloseIconBtn = styled(IconButton)`
    && {
        display: none;
        padding: 6px;
    }
    @media (max-width: 481px) {
        && {
            display: inline-flex;
        }
    }
`;

const CloseIcon = styled(Icon)`
    && {
        width: 24px;
        height: 18px;
        font-size: 24px !important;
        line-height: 24px !important;
    }
    &&::before {
        font-size: 24px !important;
        line-height: 18px !important;
    }
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const ListItemStyled = styled(ListItem)`
    margin-bottom: 8px;
    padding-left: 0 !important;
    padding-right: 0 !important;
    @media (max-width: 481px) {
        padding-left: 0px !important;
        padding-right: 0px !important;
        margin-bottom: 8px;
        align-items: flex-start;
        & .MuiListItemAvatar-root {
            min-width: 36px;
            align-self: flex-start;
            margin-top: 0;
        }
        & .MuiAvatar-root {
            width: 24px;
            height: 24px;
            font-size: 12px;
        }
        & .MuiListItemText-root {
            margin-top: 0;
            margin-bottom: 0;
        }
        & .MuiDivider-root {
            display: none;
        }
    }
`;

const UserIcon = styled(Icon)`
    && {
        width: 16px;
        height: 16px;
        font-size: 16px !important;
        line-height: 16px !important;
    }
    &&::before {
        font-size: 16px !important;
        line-height: 16px !important;
    }
    @media (max-width: 481px) {
        && {
            display: none;
        }
    }
`;

const ItemTitle = styled.span`
    padding-left: 0.1rem;
    font-size: 14px;
    @media (max-width: 481px) {
        font-size: 13px;
    }
`;

const SubIdRow = styled(Grid)`
    margin-top: 3px;
    && {
        align-items: center;
    }
    @media (max-width: 481px) {
        && {
            display: none;
        }
    }
`;

const SubId = styled(Typography)`
    && {
        font-size: 12px;
        line-height: 16px;
        color: ${({ theme }) => theme.material.colors.text.caption};
        cursor: pointer;
    }
`;

const CopyButton = styled.button`
    background: none;
    border: 0;
    padding: 0 0 0 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.material.colors.text.caption};
    &:hover {
        color: ${({ theme }) => theme.material.colors.primary.main};
    }
    &:focus {
        outline: none;
    }
`;

const LinkIcon = styled(Icon)`
    && {
        width: 16px;
        height: 16px;
        font-size: 16px !important;
        line-height: 16px !important;
    }
    &&::before {
        font-size: 16px !important;
        line-height: 16px !important;
        color: ${({ theme }) => theme.material.colors.text.caption} !important;
    }
`;

const Timestamp = styled(Typography)`
    && {
        font-size: 14px;
        line-height: 20px;
        color: ${({ theme }) => theme.material.colors.text.secondary};
        text-align: right;
        white-space: nowrap;
        margin-left: auto;
    }
    @media (max-width: 481px) {
        display: none;
    }
`;

const MobileTimestamp = styled(Typography)`
    && {
        display: none;
        font-size: 12px;
        line-height: 16px;
        color: ${({ theme }) => theme.material.colors.text.secondary};
        margin-top: 5px;
    }
    @media (max-width: 481px) {
        && {
            display: block;
        }
    }
`;

const Status = styled.div`
    padding: 32px 24px;
    text-align: center;
    color: ${({ theme }) => theme.material.colors.text.secondary};
`;

const StyledGrid = styled(Grid)`
    display: flex;
    align-items: center;
`

const TIMESTAMP_FORMAT = 'MMMM D, YYYY h:mm A';

const ReadByDialog = ({
    open,
    onClose,
    messageId,
    receipts,
    isLoading,
    error,
    userRefs,
    loadMessageReadReceipts,
    showToastr,
    theme,
}) => {
    const closeIconColor = theme.material.colors.text.closeIcon;
    useEffect(() => {
        if (open && messageId) {
            loadMessageReadReceipts(messageId);
        }
    }, [open, messageId, loadMessageReadReceipts]);

    const handleCopyId = useCallback(
        async id => {
            if (!id) return;
            try {
                await copyToClipboard(id);
                showToastr({ severity: 'success', detail: 'ID copied to clipboard' });
            } catch (e) {
                showToastr({ severity: 'error', detail: 'ID could not copied to clipboard' });
            }
        },
        [showToastr]
    );

    const handleCopyLink = useCallback(
        async platformUserId => {
            if (!platformUserId) return;
            const link = `${window.location.origin}/#/user-management/${platformUserId}`;
            try {
                await copyToClipboard(link);
                showToastr({ severity: 'success', detail: 'Link copied to clipboard' });
            } catch (e) {
                showToastr({ severity: 'error', detail: 'Link could not copied to clipboard' });
            }
        },
        [showToastr]
    );

    if (!open) return null;

    const rows =  receipts;

    let body = null;
    if (error) {
        body = (
            <Status>
                <Typography variant="body2">Read receipts not enabled on this server.</Typography>
                <Button color="primary" onClick={() => loadMessageReadReceipts(messageId)}>
                    Retry
                </Button>
            </Status>
        );
    } else if (rows?.length) {
        body = (
            <Body>
                {rows.map(r => {
                    const name = get(r, 'user.name') || get(r, 'user.username') || 'Deleted user';
                    const username = get(r, 'user.username');
                    const userRef = username ? (userRefs || []).find(u => u.username === username) : null;
                    const fullId = userRef?.id || '';
                    const subId = fullId.slice(0, 8);
                    const avatarSrc = userRef && userRef.image ? getAttachmentUrl(userRef.id, 'user', userRef.image) : null;
                    return (
                        <ListItemStyled key={r._id} dense data-testid="read-by-row">
                            <ListItemAvatar>
                                <MuiAvatar src={avatarSrc} initials={name} alt={name} />
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <StyledGrid container >
                                        <UserIcon name="account" size="sm" />
                                        <CardDivider flexItem orientation="vertical" />
                                        <ItemTitle>{name}</ItemTitle>
                                    </StyledGrid>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                                secondary={
                                    <>
                                        {subId ? (
                                            <SubIdRow container>
                                                <Tooltip title="Copy ID to clipboard">
                                                    <SubId onClick={() => handleCopyId(fullId)}>#{subId}</SubId>
                                                </Tooltip>
                                                {userRef?.id && (
                                                    <Tooltip title="Copy link to clipboard">
                                                        <CopyButton
                                                            type="button"
                                                            aria-label="Copy user link"
                                                            onClick={() => handleCopyLink(userRef.id)}
                                                        >
                                                            <LinkIcon name="link" size="sm" />
                                                        </CopyButton>
                                                    </Tooltip>
                                                )}
                                            </SubIdRow>
                                        ) : null}
                                        <MobileTimestamp>{formatDate(r.ts, TIMESTAMP_FORMAT)}</MobileTimestamp>
                                    </>
                                }
                            />
                            <Timestamp>{formatDate(r.ts, TIMESTAMP_FORMAT)}</Timestamp>
                        </ListItemStyled>
                    );
                })}
            </Body>
        );
    }

    return (
        <StyledModalDialog
            open={open}
            onClose={onClose}
            withoutClose
            showDividerAfterTitle
            isLoading={isLoading}
            maxWidth="md"
            title={<TitleText>Read By</TitleText>}
            actions={
                <>
                    <CloseButton onClick={onClose} variant="text">
                        Close
                    </CloseButton>
                    <CloseIconBtn aria-label="close" onClick={onClose}>
                        <CloseIcon name="close" hexColor={closeIconColor} />
                    </CloseIconBtn>
                </>
            }
        >
            {body}
        </StyledModalDialog>
    );
};

ReadByDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    messageId: PropTypes.string,
    receipts: PropTypes.array.isRequired,
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    userRefs: PropTypes.array,
    loadMessageReadReceipts: PropTypes.func.isRequired,
    showToastr: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
    const entry = get(state, `chat.receipts.${ownProps.messageId}`) || {};
    return {
        receipts: entry.data || [],
        isLoading: !!entry.isLoading,
        error: entry.error || null,
        userRefs: get(state, 'admin.users.references.data') || [],
    };
};

export default connect(mapStateToProps, { loadMessageReadReceipts, showToastr })(withTheme(ReadByDialog));
